import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { sb } from '../../lib/supabase';
import { useSupabaseAuth } from '../../context/AuthContext';
import useWorkspaceAccess from '../../hooks/useWorkspaceAccess';
import Loader from '../../components/Loader';
import InitiationLayout from '../../components/InitiationLayout';
import Badge from '../../components/Badge';
import {
  fetchProjectOptions,
  fitGapSummary,
  fetchOptionCostData,
  fetchOptionRomSnapshot,
  promoteWinningOption,
  OPTION_STATUS_LABEL,
} from '../../lib/solutionOptions';
import { calcOptionTCO, formatCurrency, COST_CATEGORY_LABEL } from '../../lib/costEngine';
import { OOTB_CLASSIFICATION_LABEL } from '../../lib/initiation';

export default function BakeOffComparison() {
  const { project, programme } = useOutletContext();
  const { profile } = useSupabaseAuth();
  const { canWrite } = useWorkspaceAccess();

  const [options, setOptions] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [showDecision, setShowDecision] = useState(false);
  const [decisionForm, setDecisionForm] = useState({ winnerId: '', rationale: '', rejectedReason: '' });
  const [existingDecision, setExistingDecision] = useState(null);

  const showToast = (msg, type = 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    if (!project?.id || !programme?.id) return;
    setLoading(true);
    try {
      const [opts, reqsRes, decisionRes] = await Promise.all([
        fetchProjectOptions(project.id, programme.id),
        sb.from('requirements').select('id').eq('project_id', project.id),
        sb.from('governance_decisions')
          .select('*, selected_architecture_option_id')
          .eq('project_id', project.id)
          .eq('decision_type', 'scenario_selection')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);

      const assessed = (opts || []).filter((o) => ['assessed', 'selected'].includes(o.status));
      setOptions(assessed);
      setExistingDecision(decisionRes.data);

      const colData = await Promise.all(
        assessed.map(async (opt) => {
          const [assessRes, costs, rom] = await Promise.all([
            sb.from('requirement_option_assessments').select('*').eq('architecture_option_id', opt.id),
            fetchOptionCostData(opt.id, project.id, programme.id),
            fetchOptionRomSnapshot(opt.id, project.id, programme.id),
          ]);
          const assessMap = new Map((assessRes.data || []).map((a) => [a.requirement_id, a]));
          const summary = fitGapSummary(assessMap, reqsRes.data || []);
          const tco = calcOptionTCO({
            oneTime: costs.oneTime,
            recurringTotal: costs.recurring.fiveYearTotal,
            effortCost: rom.expected,
          });
          return { opt, summary, costs, rom, tco };
        })
      );
      setColumns(colData);
    } catch (e) {
      console.error('[BakeOffComparison] load failed', e);
      showToast(e.message || 'Failed to load bake-off data');
    } finally {
      setLoading(false);
    }
  }, [project?.id, programme?.id]);

  useEffect(() => { load(); }, [load]);

  async function recordDecision() {
    if (!decisionForm.winnerId || !decisionForm.rationale.trim()) return;
    setSaving(true);
    try {
      const winner = options.find((o) => o.id === decisionForm.winnerId);
      const { error: decErr } = await sb.from('governance_decisions').insert({
        programme_id: programme.id,
        project_id: project.id,
        title: `Solution scenario selection: ${winner?.name}`,
        description: 'Bake-off decision selecting the preferred solution option.',
        status: 'made',
        decision_type: 'scenario_selection',
        selected_architecture_option_id: decisionForm.winnerId,
        rationale: decisionForm.rationale.trim(),
        decided_by_name: profile?.full_name || profile?.email || 'Unknown',
        decision_date: new Date().toISOString().slice(0, 10),
        created_by: profile?.id,
      });
      if (decErr) throw decErr;

      for (const col of columns) {
        if (col.opt.id !== decisionForm.winnerId) {
          await sb.from('architecture_options').update({
            status: 'rejected',
            rejected_reason: decisionForm.rejectedReason.trim() || 'Not selected in bake-off',
            is_winner: false,
          }).eq('id', col.opt.id);
        }
      }

      await promoteWinningOption(decisionForm.winnerId, project.id, programme.id, profile?.id);
      setShowDecision(false);
      await load();
      showToast('Scenario decision recorded — winner promoted', 'success');
    } catch (e) {
      showToast(e.message || 'Decision failed');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <Loader text="Loading bake-off comparison…" />;

  const winner = options.find((o) => o.status === 'selected');

  return (
    <InitiationLayout
      title="Bake-off comparison"
      subtitle={`${project?.name} · Side-by-side option assessment`}
      badge={winner ? <Badge variant="gr">Winner: {winner.name}</Badge> : <Badge variant="am">{options.length} options</Badge>}
      actions={
        canWrite && !existingDecision && options.length >= 1 ? (
          <button type="button" className="btn-primary" onClick={() => setShowDecision(true)}>
            Record scenario decision
          </button>
        ) : null
      }
    >
      {options.length === 0 && (
        <div className="gate-banner warn">
          No assessed options yet. Mark at least one option as assessed in the Solution options workspace.
        </div>
      )}

      {existingDecision && (
        <div className="approval-banner" style={{ marginBottom: 12 }}>
          <span className="approval-banner-icon">✓</span>
          <span className="approval-banner-text">
            Scenario decision recorded{existingDecision.rationale ? `: ${existingDecision.rationale}` : '.'}
          </span>
        </div>
      )}

      {columns.length > 0 && (
        <div className="bo-scroll">
          <table className="bo-table">
            <thead>
              <tr>
                <th className="bo-metric-col">Metric</th>
                {columns.map(({ opt }) => (
                  <th key={opt.id} className={opt.status === 'selected' ? 'bo-col-winner' : ''}>
                    <div className="bo-col-title">{opt.name}</div>
                    <Badge variant={opt.status === 'selected' ? 'gr' : 'pu'}>{OPTION_STATUS_LABEL[opt.status]}</Badge>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>OOTB score</td>
                {columns.map(({ opt, summary }) => (
                  <td key={opt.id}>{summary.score != null ? `${summary.score}%` : '—'}</td>
                ))}
              </tr>
              {Object.entries(OOTB_CLASSIFICATION_LABEL).map(([key, label]) => (
                <tr key={key}>
                  <td>{label} count</td>
                  {columns.map(({ opt, summary }) => (
                    <td key={opt.id}>{summary.counts[key] || 0}</td>
                  ))}
                </tr>
              ))}
              <tr>
                <td>Interfaces</td>
                {columns.map(({ opt }) => (
                  <td key={opt.id}>{opt.interface_count ?? 0}</td>
                ))}
              </tr>
              <tr>
                <td>Effort ROM (PERT)</td>
                {columns.map(({ opt, rom }) => (
                  <td key={opt.id}>{formatCurrency(rom.expected)}</td>
                ))}
              </tr>
              <tr>
                <td>One-time CapEx</td>
                {columns.map(({ opt, costs }) => (
                  <td key={opt.id}>{formatCurrency(costs.oneTime.capex)}</td>
                ))}
              </tr>
              <tr>
                <td>One-time OpEx</td>
                {columns.map(({ opt, costs }) => (
                  <td key={opt.id}>{formatCurrency(costs.oneTime.opex)}</td>
                ))}
              </tr>
              <tr>
                <td>5-yr recurring</td>
                {columns.map(({ opt, costs }) => (
                  <td key={opt.id}>{formatCurrency(costs.recurring.fiveYearTotal)}</td>
                ))}
              </tr>
              {Object.keys(COST_CATEGORY_LABEL).map((cat) => (
                <tr key={cat}>
                  <td>Recurring Y1 · {COST_CATEGORY_LABEL[cat]}</td>
                  {columns.map(({ opt, costs }) => (
                    <td key={opt.id}>{formatCurrency(costs.recurring.byYearCategory[1]?.[cat] || 0)}</td>
                  ))}
                </tr>
              ))}
              <tr className="bo-total-row">
                <td><strong>TCO (cash basis)</strong></td>
                {columns.map(({ opt, tco }) => (
                  <td key={opt.id}><strong>{formatCurrency(tco)}</strong></td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <p style={{ fontSize: 10.5, color: '#888780', marginTop: 12 }}>
        TCO = effort ROM + one-time costs + 5-year recurring. CapEx/OpEx split is informational — no depreciation calculated.
      </p>

      {showDecision && (
        <div className="modal-overlay" onClick={() => setShowDecision(false)} role="presentation">
          <div className="modal-card" onClick={(e) => e.stopPropagation()} role="dialog">
            <div className="modal-hdr">Scenario decision</div>
            <div className="form-row">
              <label className="form-label">Winning option *</label>
              <select
                className="form-select"
                value={decisionForm.winnerId}
                onChange={(e) => setDecisionForm((p) => ({ ...p, winnerId: e.target.value }))}
              >
                <option value="">Select…</option>
                {options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
            <div className="form-row">
              <label className="form-label">Rationale *</label>
              <textarea
                className="form-input"
                rows={4}
                value={decisionForm.rationale}
                onChange={(e) => setDecisionForm((p) => ({ ...p, rationale: e.target.value }))}
                placeholder="Why this option, why not the others?"
              />
            </div>
            <div className="form-row">
              <label className="form-label">Rejection note (for non-winners)</label>
              <input
                className="form-input"
                value={decisionForm.rejectedReason}
                onChange={(e) => setDecisionForm((p) => ({ ...p, rejectedReason: e.target.value }))}
              />
            </div>
            <div className="modal-foot">
              <button type="button" className="btn-ghost" onClick={() => setShowDecision(false)}>Cancel</button>
              <button
                type="button"
                className="btn-primary"
                disabled={saving || !decisionForm.winnerId || !decisionForm.rationale.trim()}
                onClick={recordDecision}
              >
                {saving ? 'Saving…' : 'Confirm decision'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className={toast.type === 'success' ? 'toast-success' : 'toast-error'}>{toast.msg}</div>
      )}
    </InitiationLayout>
  );
}
